import type Quill from 'quill';
import sanitizeHtml from 'sanitize-html';

import { convertRgbToHex } from 'src/utils/html.utils';

// ----------------------------------------------------------------------

/**
 * Paste clipboard content into the editor at the current caret.
 *
 * Rich HTML is run through a strict allow-list (and rgb→hex normalisation);
 * plain text is inserted verbatim. Any selected range is removed first, so the
 * behaviour mirrors the browser's native paste.
 *
 * Note: this inserts at the selection index. The single-argument
 * `dangerouslyPasteHTML(html)` form replaces the ENTIRE document — using it here
 * is what previously wiped already-typed content on paste.
 */
export const insertSanitizedPaste = (editor: Quill, event: ClipboardEvent): void => {
  event.preventDefault();

  const { clipboardData } = event;
  if (!clipboardData) {
    console.warn('No clipboard data available');
    return;
  }

  const pasteAtCursor = (content: string, asHtml: boolean): void => {
    const range = editor.getSelection(true);
    const index = range ? range.index : editor.getLength();

    // Replace the current selection, like a native paste.
    if (range && range.length > 0) {
      editor.deleteText(range.index, range.length, 'user');
    }

    if (asHtml) {
      // Two-argument form inserts at `index` instead of replacing the document.
      editor.clipboard.dangerouslyPasteHTML(index, content, 'user');
    } else {
      editor.insertText(index, content, 'user');
      editor.setSelection(index + content.length, 0);
    }
  };

  // Prefer sanitised HTML when the clipboard provides it.
  if (clipboardData.types.includes('text/html')) {
    try {
      let sanitizedHtml = sanitizeHtml(clipboardData.getData('text/html'), {
        allowedTags: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'div', 'span', 'ul', 'ol', 'li'],
        allowedAttributes: {
          a: ['href'],
        },
      });

      // Convert rgb colours to hex.
      sanitizedHtml = convertRgbToHex(sanitizedHtml);

      if (sanitizedHtml.trim()) {
        pasteAtCursor(sanitizedHtml, true);
        return;
      }
    } catch (e) {
      console.error('Error processing HTML clipboard content:', e);
      // Fall through to plain-text handling.
    }
  }

  // Fall back to plain text when there is no usable HTML.
  try {
    const text = clipboardData.getData('text/plain');
    if (text.trim()) {
      pasteAtCursor(text, false);
      return;
    }
  } catch (e) {
    console.error('Error processing plain text clipboard content:', e);
  }

  // Nothing usable was found.
  console.warn('No valid content found in clipboard');
};
